/**
 * SubmitButton (TMMPDM-0034, ACT-008 / FRM-014 / PERF-001): the one button for a consequential
 * submit — create, save, activate, close, assign, record, invite, remove… While `busy` it shows
 * MUI's in-button progress (`loading` — a progressbar labelled by the button) and is disabled, so a
 * second click cannot submit twice; a polite live region beside it says "Saving…" to assistive
 * tech. The accessible name stays the verb label throughout.
 *
 * TMMPDM-0065 (TMM-B5, FRM-005): a failed submit focuses the first error. After a click, and when
 * a submit finishes (`busy` goes true → false), focus moves to the first control in the same
 * dialog (or form) that is `aria-invalid`. Only a submit moves focus — never a re-render or a
 * keystroke — and a page form that shows an error summary (`data-error-summary`) keeps the focus
 * on the summary instead.
 */
import { useEffect, useRef } from 'react';
import { Box, Button, type ButtonProps } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

export interface SubmitButtonProps extends Omit<ButtonProps, 'loading'> {
  busy: boolean;
}

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** Focus the first `aria-invalid` control in the dialog or form the button sits in. */
function focusFirstError(from: Element | null) {
  const scope = from?.closest('[role="dialog"]') ?? from?.closest('form');
  if (!scope || scope.querySelector('[data-error-summary]')) return;
  const invalid = scope.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!invalid) return;
  // A MUI select's invalid flag can land on its hidden native input: focus the combobox beside it.
  const target =
    invalid.getAttribute('aria-hidden') === 'true'
      ? (invalid.parentElement?.querySelector<HTMLElement>('[role="combobox"]') ?? invalid)
      : invalid;
  if (document.activeElement !== target) target.focus();
}

export function SubmitButton({
  busy,
  disabled,
  children,
  variant = 'contained',
  onClick,
  ...rest
}: SubmitButtonProps) {
  const { t } = useCopy();
  const anchor = useRef<HTMLSpanElement>(null);
  const wasBusy = useRef(false);

  useEffect(() => {
    if (wasBusy.current && !busy) focusFirstError(anchor.current);
    wasBusy.current = busy;
  }, [busy]);

  return (
    <>
      <Button
        variant={variant}
        {...rest}
        onClick={(event) => {
          onClick?.(event);
          // After the click's state updates have committed (a client-side check failed).
          setTimeout(() => focusFirstError(anchor.current), 0);
        }}
        loading={busy}
        disabled={busy || Boolean(disabled)}
      >
        {children}
      </Button>
      <Box component="span" ref={anchor} aria-live="polite" sx={visuallyHidden}>
        {busy ? t('components.saving', 'Saving…') : ''}
      </Box>
    </>
  );
}
